import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Search, Upload, Check, Monitor, X, Loader2, Users, Gauge, Play, Square, Maximize2 } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@meno/ui/components/ui/select'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@meno/ui/components/ui/dialog'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { usePermissions } from '@meno/ui/hooks/usePermissions'
import { getScreens, getScreenGroups, publishTransition } from '@meno/ui/lib/api'
import { ContentCard, type CardMenuItem } from '@/components/ContentCard'
import {
  TRANSITIONS, DEFAULT_TRANSITION_KEY, TRANSITION_SPEEDS,
  type Transition, type TransitionSpeed,
} from '@/lib/transitions'
import {
  TransitionPreview, TransitionPreviewStyles, SPEED_PREVIEW_DURATION,
} from '@/components/TransitionPreview'

// ── Transition catalog ──────────────────────────────────────────────────────
// The catalog lives in lib/transitions.ts (shared with the publish-companion
// pickers); the animated preview primitives (keyframes + the two-slide stage)
// live in components/TransitionPreview.tsx so this page and the picker modal in
// the create/edit dialogs share one source. A transition is a per-screen setting
// persisted on the backend (Screen.display_settings.transition) and applied by
// the players; "publish" calls POST /transitions/publish and "active" is derived
// from the screens' display_settings.
const CATEGORIES = Array.from(new Set(TRANSITIONS.map(t => t.category)))

type ScreenRow = { id: number; name: string }
type GroupRow = { id: number; name: string; screenIds: number[] }

export default function TransitionsPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const canPublish = can('playlists:edit')  // applying a transition publishes to screens
  usePageHeader({
    title: t('transitions.title', 'Transitions'),
    subtitle: t('transitions.subtitle', 'Control how content animates between items on your screens'),
  })

  const [screens, setScreens] = useState<ScreenRow[]>([])
  const [groups, setGroups] = useState<GroupRow[]>([])
  const [loadingScreens, setLoadingScreens] = useState(true)
  // screenId → transition key, hydrated from localStorage. Drives usage counts.
  const [assignments, setAssignments] = useState<Record<number, string>>({})
  const [search, setSearch] = useState('')
  // Category (type) filter — a single-select dropdown; 'all' shows every type.
  const [category, setCategory] = useState('all')
  const [activeOnly, setActiveOnly] = useState(false)
  // Speed sent with every publish from this page (per-screen, reaches players).
  const [speed, setSpeed] = useState<TransitionSpeed>('normal')
  const [publishFor, setPublishFor] = useState<Transition | null>(null)
  // Global preview playback: when on, EVERY card's preview loops (not just on
  // hover). Changing the speed turns it on so the operator immediately sees the
  // previews play at the new speed. `enlarged` drives the blown-up preview modal.
  const [playing, setPlaying] = useState(false)
  const [enlarged, setEnlarged] = useState<Transition | null>(null)
  const changeSpeed = (sp: TransitionSpeed) => { setSpeed(sp); setPlaying(true) }

  // A screen's transition comes from display_settings.transition; a screen with
  // none set is on the Default baseline.
  const loadScreens = () => {
    setLoadingScreens(true)
    Promise.all([getScreens(), getScreenGroups().catch(() => ({ data: [] }))])
      .then(([sr, gr]) => {
        const list: ScreenRow[] = (sr.data || []).map((s: any) => ({ id: s.id, name: s.name }))
        setScreens(list)
        setGroups((gr.data || []).map((g: any) => ({
          id: g.id, name: g.name, screenIds: (g.screens || []).map((s: any) => s.id),
        })))
        const a: Record<number, string> = {}
        for (const s of (sr.data || [])) {
          a[s.id] = s.display_settings?.transition || DEFAULT_TRANSITION_KEY
        }
        setAssignments(a)
      })
      .catch(() => { /* screens are optional — publish just shows an empty list */ })
      .finally(() => setLoadingScreens(false))
  }

  useEffect(loadScreens, [])

  // Transition key → number of screens currently using it.
  const usage = useMemo(() => {
    const u: Record<string, number> = {}
    for (const k of Object.values(assignments)) u[k] = (u[k] ?? 0) + 1
    return u
  }, [assignments])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return TRANSITIONS.filter(tr => {
      if (category !== 'all' && tr.category !== category) return false
      if (activeOnly && !(usage[tr.key] > 0)) return false
      if (q && !tr.name.toLowerCase().includes(q) && !tr.category.toLowerCase().includes(q)) return false
      return true
    })
  }, [search, category, activeOnly, usage])

  const activeCount = useMemo(
    () => TRANSITIONS.filter(tr => usage[tr.key] > 0).length,
    [usage],
  )

  // Apply the publish dialog's selection: selected screens get this transition
  // (at the current page speed); any screen that had it but is now unchecked
  // reverts to the Default baseline. Both go through the backend.
  const applyPublish = async (transition: Transition, selected: Set<number>) => {
    const cleared = screens
      .filter(s => assignments[s.id] === transition.key && !selected.has(s.id))
      .map(s => s.id)
    setPublishFor(null)
    try {
      if (selected.size > 0) {
        await publishTransition({
          key: transition.key, name: transition.name, speed,
          screen_ids: [...selected],
        })
      }
      if (cleared.length > 0) {
        await publishTransition({ key: DEFAULT_TRANSITION_KEY, speed, screen_ids: cleared })
      }
      loadScreens()  // re-derive active counts from the persisted settings
      toast.success(
        selected.size > 0
          ? t('transitions.published', { defaultValue: '“{{name}}” applied to {{count}} screen', name: transition.name, count: selected.size })
          : t('transitions.cleared', { defaultValue: '“{{name}}” removed from all screens', name: transition.name }),
      )
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('common.error', 'Something went wrong'))
    }
  }

  return (
    // --tr-dur cascades into every card preview's .tr-in animation, so the
    // selected speed changes how fast the previews play.
    <div className="w-full space-y-5" style={{ '--tr-dur': SPEED_PREVIEW_DURATION[speed] } as CSSProperties}>
      <TransitionPreviewStyles />

      {/* Toolbar — search + category filter + active toggle */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 min-w-0 max-w-md">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('transitions.search', 'Search transitions…')}
              className="ps-9 h-10"
            />
          </div>
          <button
            type="button"
            onClick={() => setActiveOnly(v => !v)}
            aria-pressed={activeOnly}
            className={`h-10 px-3 rounded-lg border text-sm font-medium transition-colors shrink-0 inline-flex items-center gap-2 ${
              activeOnly ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${activeCount > 0 ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
            {t('transitions.activeOnly', 'Active')}
            <span className="text-xs text-muted-foreground">{activeCount}</span>
          </button>

          {/* Play / Stop — toggles looping previews across every card. */}
          <button
            type="button"
            onClick={() => setPlaying(p => !p)}
            aria-pressed={playing}
            className={`h-10 px-3 rounded-lg border text-sm font-medium transition-colors shrink-0 inline-flex items-center gap-2 ${
              playing ? 'border-primary bg-primary/10 text-foreground' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            {playing ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {playing ? t('transitions.stop', 'Stop') : t('transitions.play', 'Play')}
          </button>

          {/* Speed — applied to every publish from this page (per-screen, sent
              to the players in the manifest). Changing it plays the previews so
              the new speed is immediately visible. */}
          <div className="h-10 shrink-0 inline-flex items-center gap-1 rounded-lg border border-border bg-muted/30 px-1.5">
            <Gauge className="w-4 h-4 text-muted-foreground ms-1" />
            {TRANSITION_SPEEDS.map(sp => (
              <button
                key={sp}
                type="button"
                onClick={() => changeSpeed(sp)}
                aria-pressed={speed === sp}
                className={`h-7 px-2.5 rounded-md text-xs font-medium capitalize transition-colors ${
                  speed === sp ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t(`transitions.speed.${sp}`, sp)}
              </button>
            ))}
          </div>
        </div>

        {/* Type filter — a single-select dropdown of transition categories. */}
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground shrink-0">{t('transitions.type', 'Type')}</span>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[200px] h-9 bg-background">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('transitions.allTypes', 'All types')}</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-sm text-muted-foreground">
          {t('common.noData', 'No data found')}
        </div>
      ) : (
        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 ${playing ? 'tr-playing' : ''}`}>
          {filtered.map(tr => {
            const count = usage[tr.key] ?? 0
            const active = count > 0
            const isDefault = tr.key === DEFAULT_TRANSITION_KEY
            // Default/active emphasis rides on ContentCard's merged className so
            // the shell stays shared while transitions keep their ring accent.
            const accent = isDefault
              ? 'border border-primary ring-2 ring-primary/40'
              : active
                ? 'border border-primary ring-1 ring-primary/30'
                : 'border border-border hover:border-primary/40'
            const menu: CardMenuItem[] = [
              { icon: Maximize2, label: t('transitions.enlarge', 'Enlarge preview'), onClick: () => setEnlarged(tr) },
            ]
            return (
              <ContentCard
                key={tr.key}
                title={tr.name}
                subtitle={active
                  ? `${tr.category} · ${t('contentHub.onScreens', '{{count}} screens', { count })}`
                  : tr.category}
                onOpen={() => setEnlarged(tr)}
                onPublish={canPublish ? () => setPublishFor(tr) : undefined}
                publishLabel={active ? t('transitions.manage', 'Manage') : t('playlist.publish', 'Publish')}
                menu={menu}
                className={accent}
                media={
                  // Animated preview — the shared two-slide stage shows what a
                  // transition does: the outgoing slide sits under the incoming
                  // red slide that animates in on hover. Badges overlay it.
                  <div className="relative h-24">
                    <TransitionPreview anim={tr.anim} className="h-24" logoClass="h-7" />
                    {isDefault && (
                      <span className="absolute top-1.5 start-1.5 z-20 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground">
                        {t('transitions.default', 'Default')}
                      </span>
                    )}
                    {active && (
                      <span className="absolute top-1.5 end-1.5 z-10 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-emerald-500/90 text-white">
                        <Check className="w-2.5 h-2.5" />
                        {t('transitions.active', 'Active')}
                      </span>
                    )}
                  </div>
                }
              />
            )
          })}
        </div>
      )}

      {publishFor && (
        <PublishTransitionDialog
          transition={publishFor}
          screens={screens}
          groups={groups}
          loading={loadingScreens}
          assignments={assignments}
          onClose={() => setPublishFor(null)}
          onApply={applyPublish}
        />
      )}

      {/* Enlarged preview — a big, always-playing version of the picked
          transition. The Dialog portals outside the page root, so the preview
          wrapper re-declares --tr-dur (and .tr-playing) to keep the animation
          + current speed. */}
      <Dialog open={!!enlarged} onOpenChange={o => { if (!o) setEnlarged(null) }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{enlarged?.name}</DialogTitle>
          </DialogHeader>
          {enlarged && (
            <div className="tr-playing" style={{ '--tr-dur': SPEED_PREVIEW_DURATION[speed] } as CSSProperties}>
              <TransitionPreview
                anim={enlarged.anim}
                className="w-full aspect-video rounded-lg border border-border"
                logoClass="h-16"
              />
              <p className="text-xs text-muted-foreground mt-3 text-center">
                {enlarged.category} · {t(`transitions.speed.${speed}`, speed)}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Publish dialog ───────────────────────────────────────────────────────────
// Pick the screens that should use this transition. Screens already using it
// are pre-checked; unchecking one clears it back to no transition.
function PublishTransitionDialog({
  transition, screens, groups, loading, assignments, onClose, onApply,
}: {
  transition: Transition
  screens: ScreenRow[]
  groups: GroupRow[]
  loading: boolean
  assignments: Record<number, string>
  onClose: () => void
  onApply: (transition: Transition, selected: Set<number>) => void
}) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'screens' | 'groups'>('screens')
  const [selected, setSelected] = useState<Set<number>>(() =>
    new Set(screens.filter(s => assignments[s.id] === transition.key).map(s => s.id)),
  )
  const toggle = (id: number) => setSelected(prev => {
    const n = new Set(prev)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })
  // A group is "on" when all its (non-empty) members are selected. Toggling it
  // selects or clears every member — groups are just a bulk-select over the
  // same screen-id set the apply step consumes.
  const groupState = (g: GroupRow) =>
    g.screenIds.length > 0 && g.screenIds.every(id => selected.has(id))
  const toggleGroup = (g: GroupRow) => setSelected(prev => {
    const n = new Set(prev)
    const on = g.screenIds.length > 0 && g.screenIds.every(id => n.has(id))
    for (const id of g.screenIds) { if (on) n.delete(id); else n.add(id) }
    return n
  })

  const TabBtn = ({ id, icon: Icon, label, count }: { id: 'screens' | 'groups'; icon: typeof Monitor; label: string; count: number }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`px-3 h-7 text-xs font-medium rounded inline-flex items-center gap-1.5 transition-colors ${
        tab === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      {count > 0 && <span className="ms-0.5 text-[10px] text-muted-foreground">{count}</span>}
    </button>
  )

  const selectedGroupCount = groups.filter(groupState).length

  // Hand-built overlay, so it carries the dialog semantics a Radix Dialog
  // would: announced as a modal named by its title, and Escape closes it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-transition-title"
        className="bg-card rounded-xl border border-border w-full max-w-md shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-5 pb-3">
          <div className="min-w-0">
            <h3 id="publish-transition-title" className="text-base font-semibold flex items-center gap-2">
              <Upload className="w-4 h-4 text-primary" />
              {t('transitions.publishTitle', 'Publish transition')}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">
              {t('transitions.publishDesc', { defaultValue: 'Choose the screens or groups that should use “{{name}}”.', name: transition.name })}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label={t('common.close', 'Close')}
            className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Screens / Groups tab switcher — mirrors the shared PublishDialog. */}
        <div className="px-5 pb-2">
          <div className="inline-flex rounded-md border border-border bg-muted/40 p-0.5">
            <TabBtn id="screens" icon={Monitor} label={t('playlist.scheduleScreens', 'Screens')} count={selected.size} />
            <TabBtn id="groups" icon={Users} label={t('playlist.scheduleGroups', 'Groups')} count={selectedGroupCount} />
          </div>
        </div>

        <div className="max-h-72 overflow-y-auto border-y border-border">
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : tab === 'screens' ? (
            screens.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-10">{t('transitions.noScreens', 'No screens yet.')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {screens.map(s => {
                  const checked = selected.has(s.id)
                  const other = assignments[s.id] && assignments[s.id] !== transition.key
                  return (
                    <li key={s.id}>
                      <button type="button" onClick={() => toggle(s.id)}
                        className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-start transition-colors ${checked ? 'bg-muted' : 'hover:bg-muted/40'}`}>
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-primary border-primary text-primary-foreground' : 'border-border'}`}>
                          {checked && <Check className="w-3 h-3" />}
                        </span>
                        <Monitor className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate flex-1">{s.name}</span>
                        {other && (
                          <span className="text-[10px] text-muted-foreground shrink-0">
                            {t('transitions.usesOther', 'other')}
                          </span>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            )
          ) : (
            groups.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-10">{t('playlist.scheduleNoGroups', 'No groups available.')}</p>
            ) : (
              <ul className="divide-y divide-border">
                {groups.map(g => {
                  const checked = groupState(g)
                  return (
                    <li key={g.id}>
                      <button type="button" onClick={() => toggleGroup(g)}
                        className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-start transition-colors ${checked ? 'bg-muted' : 'hover:bg-muted/40'}`}>
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-primary border-primary text-primary-foreground' : 'border-border'}`}>
                          {checked && <Check className="w-3 h-3" />}
                        </span>
                        <Users className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate flex-1">{g.name}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0">
                          {t('transitions.groupScreens', { defaultValue: '{{count}} screen', count: g.screenIds.length })}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )
          )}
        </div>

        <div className="flex justify-end gap-2 p-4">
          <Button variant="outline" size="sm" onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
          <Button size="sm" className="gap-1.5" onClick={() => onApply(transition, selected)}>
            <Check className="w-4 h-4" />
            {t('common.apply', 'Apply')}
          </Button>
        </div>
      </div>
    </div>
  )
}
